import { ApolloLink, FetchResult, Observable } from '@apollo/client';
import { print } from 'graphql';
import { createClient, ClientOptions } from 'graphql-sse';
import { UserLoadedCallback } from 'oidc-client-ts';

import { userManager } from '~/services/oidc';

const createSseLink = (linkOptions: ClientOptions) => {
  return new ApolloLink((operation) => {
    return new Observable((observer) => {
      const createClientAndSubscribe = (): ((renew?: boolean) => void) => {
        const context = operation.getContext();
        let closed = false;
        const client = createClient({
          ...linkOptions,
          // The datagraph (singular) authenticates via `Authorization: Bearer`, not cookies.
          // Sending credentials:'include' forces the browser to require Access-Control-Allow-
          // Credentials, which the Bearer-only gateway (correctly) does not return → the browser
          // drops the response ("CORS Missing Allow Credentials"). Omit credentials here.
          credentials: 'omit',
          headers: {
            ...context.headers,
            ...linkOptions.headers,
          },
        });

        const unsubscribe = client.subscribe<FetchResult>(
          { ...operation, query: print(operation.query) },
          {
            next: (value) => {
              if (!closed) {
                observer.next(value as any);
              }
            },
            complete: () => {
              if (!closed) {
                observer.complete();
              }
            },
            error: (error: unknown) => {
              if (!closed) {
                observer.error(error);
              }
            },
          },
        );

        return (renew) => {
          if (renew) {
            // mark the old SSE client as closed so that connected events (next/complete/error)
            // of the old SSE client are not passed through to the Observable when unsubscribing that
            closed = true;
          }
          unsubscribe();
          if (renew) {
            client.dispose();
          }
        };
      };

      let clientUnsubscribe = createClientAndSubscribe();

      // register event to renew the SSE client once a new user token gets assigned
      const eventHandler: UserLoadedCallback = () => {
        clientUnsubscribe(true);
        clientUnsubscribe = createClientAndSubscribe();
      };
      userManager.events.addUserLoaded(eventHandler);
      return () => {
        clientUnsubscribe();
        userManager.events.removeUserLoaded(eventHandler);
      };
    });
  });
};

export class SseLink extends ApolloLink {
  constructor(opts: ClientOptions) {
    super(createSseLink(opts).request);
  }
}
